import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Trees, Heart, Euro, Plus, Calendar, User } from "lucide-react";
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

interface SocialAction {
  id: string;
  tenant_id: string;
  social_project_id: string;
  obligation_id: string;
  units_completed: number;
  amount_spent: number;
  description: string | null;
  action_date: string;
  created_at: string;
  created_by_user_id: string;
  social_projects: {
    name: string;
    project_type: string;
  } | null;
}

interface SocialProject {
  id: string;
  name: string;
  project_type: string;
}

interface AdminSocialActionsProps {
  tenantId: string;
  availableProjects: SocialProject[];
}

export function AdminSocialActions({ tenantId, availableProjects }: AdminSocialActionsProps) {
  const [actions, setActions] = useState<SocialAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    social_project_id: '',
    units_completed: 1,
    amount_spent: 0,
    description: '',
  });
  const { toast } = useToast();

  useEffect(() => {
    if (tenantId) {
      fetchActions();
    }
  }, [tenantId]);

  const fetchActions = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_social_actions')
        .select(`
          *,
          social_projects (
            name,
            project_type
          )
        `)
        .eq('tenant_id', tenantId)
        .order('action_date', { ascending: false });

      if (error) throw error;
      const validActions = (data || [])
        .filter(item => 
          item.social_projects && 
          typeof item.social_projects === 'object' && 
          item.social_projects !== null && 
          'name' in (item.social_projects || {}) &&
          'project_type' in (item.social_projects || {})
        ) as unknown as SocialAction[];
      setActions(validActions);
    } catch (error) {
      console.error('Error fetching actions:', error);
      console.error('Full error details:', error);
      toast({
        title: "Fehler beim Laden",
        description: `Aktionen konnten nicht geladen werden: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Nicht angemeldet');

      const { error } = await supabase
        .from('admin_social_actions')
        .insert({
          tenant_id: tenantId,
          social_project_id: formData.social_project_id,
          units_completed: formData.units_completed,
          amount_spent: formData.amount_spent,
          description: formData.description || null,
          created_by_user_id: user.user.id,
        });

      if (error) throw error;

      toast({
        title: "Aktion dokumentiert",
        description: "Ihre soziale Aktion wurde erfolgreich dokumentiert.",
      });

      setDialogOpen(false);
      setFormData({
        social_project_id: '',
        units_completed: 1,
        amount_spent: 0,
        description: '',
      });
      
      await fetchActions();
    } catch (error) {
      console.error('Error creating action:', error);
      toast({
        title: "Fehler",
        description: "Die Aktion konnte nicht dokumentiert werden.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getProjectIcon = (projectType: string) => {
    switch (projectType) {
      case 'trees':
        return <Trees className="h-5 w-5 text-green-600" />;
      case 'families':
      case 'family_help':
        return <Heart className="h-5 w-5 text-red-600" />;
      default:
        return <Euro className="h-5 w-5 text-blue-600" />;
    }
  };

  const getUnitName = (projectType: string) => {
    switch (projectType) {
      case 'trees':
        return 'Bäume';
      case 'families':
      case 'family_help':
        return 'Familien';
      default:
        return 'Einheiten';
    }
  };

  const totalSpent = actions.reduce((sum, action) => sum + action.amount_spent, 0);
  const totalUnits = actions.reduce((sum, action) => sum + action.units_completed, 0);

  if (loading) {
    return <div className="p-4">Lade Aktionen...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Dokumentierte Aktionen</p>
                <p className="text-2xl font-bold">{actions.length}</p>
              </div>
              <User className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Gesamtausgaben</p>
                <p className="text-2xl font-bold">{totalSpent.toFixed(2)}€</p>
              </div>
              <Euro className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unterstützte Einheiten</p>
                <p className="text-2xl font-bold">{totalUnits}</p>
              </div>
              <Trees className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle>Aktivitätsprotokooll</CardTitle>
            <CardDescription>
              Chronologische Übersicht aller durchgeführten sozialen Aktionen
            </CardDescription>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Aktion dokumentieren
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Soziale Aktion dokumentieren</DialogTitle>
                <DialogDescription>
                  Dokumentieren Sie eine durchgeführte soziale Aktion
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="project">Projekt</Label>
                  <Select
                    value={formData.social_project_id}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, social_project_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Projekt auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableProjects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          <div className="flex items-center gap-2">
                            {getProjectIcon(project.project_type)}
                            {project.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="units">Anzahl Einheiten</Label>
                  <Input
                    id="units"
                    type="number"
                    min="1"
                    value={formData.units_completed}
                    onChange={(e) => setFormData(prev => ({ ...prev, units_completed: parseInt(e.target.value) || 1 }))}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="amount">Ausgaben (€)</Label>
                  <Input
                    id="amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.amount_spent}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount_spent: parseFloat(e.target.value) || 0 }))}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Beschreibung (optional)</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Details zur durchgeführten Aktion..."
                  />
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={submitting || !formData.social_project_id}>
                    {submitting ? "Speichere..." : "Dokumentieren"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {actions.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              Noch keine Aktionen dokumentiert. Dokumentieren Sie Ihre ersten sozialen Aktionen 
              um ein vollständiges Protokoll zu erstellen.
            </p>
          ) : (
            <div className="space-y-4">
              {actions.map((action) => (
                <div
                  key={action.id}
                  className="p-4 border rounded-lg bg-card"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {getProjectIcon(action.social_projects?.project_type || 'default')}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium">{action.social_projects?.name}</h4>
                        </div>
                        
                        <p className="text-sm mb-2">
                          <span className="font-medium">{action.units_completed} {getUnitName(action.social_projects?.project_type || 'default')}</span>
                          {' '}für <span className="font-medium text-green-600">{action.amount_spent.toFixed(2)}€</span>
                        </p>
                        
                        {action.description && (
                          <p className="text-sm text-muted-foreground mb-2">
                            {action.description}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(action.action_date), 'dd.MM.yyyy HH:mm', { locale: de })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}